import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw, Loader2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

type SavedCard = { id: string; brand: string; last4: string; expMonth: number; expYear: number };

function cardLabel(c: SavedCard) {
  return `${c.brand.charAt(0).toUpperCase() + c.brand.slice(1)} ····${c.last4} ${c.expMonth}/${String(c.expYear).slice(-2)}`;
}

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

export function LeadAutoTopUpCard({ workspaceId }: { workspaceId?: string }) {
  const { toast } = useToast();

  const { data: cfg, refetch } = useQuery<{
    enabled: boolean; threshold_credits: number; pack_credits: number;
    pack_price_cents: number; stripe_payment_method_id: string | null;
    last_triggered_at: string | null;
  }>({
    queryKey: ["/api/lead-credits/auto-top-up", workspaceId],
    queryFn: () => fetch("/api/lead-credits/auto-top-up").then((r) => r.json()),
  });

  const { data: balData } = useQuery<{
    grantBalance: number; purchasedBalance: number;
    packs: Array<{ priceCents: number; credits: number; label: string }>;
  }>({
    queryKey: ["/api/lead-credits/balance", workspaceId],
    queryFn: () => fetch("/api/lead-credits/balance").then((r) => r.json()),
  });

  const { data: pmData } = useQuery<{ methods: SavedCard[] }>({
    queryKey: ["/api/billing/payment-methods", workspaceId],
    staleTime: 60_000,
  });

  const [enabled, setEnabled] = useState(false);
  const [threshold, setThreshold] = useState("50");
  const [packIdx, setPackIdx] = useState("0");
  const [pmId, setPmId] = useState("");
  const [saving, setSaving] = useState(false);

  const packs = balData?.packs ?? [];

  useEffect(() => {
    if (!cfg) return;
    setEnabled(cfg.enabled);
    setThreshold(String(cfg.threshold_credits ?? 50));
    const idx = packs.findIndex((p) => p.credits === cfg.pack_credits);
    setPackIdx(String(idx >= 0 ? idx : 0));
    setPmId(cfg.stripe_payment_method_id ?? "");
  }, [cfg, packs.length]);

  const selectedPack = packs[Number(packIdx)] ?? packs[0];

  const handleSave = async () => {
    setSaving(true);
    try {
      const r = await apiRequest("PATCH", "/api/lead-credits/auto-top-up", {
        enabled,
        thresholdCredits: Number(threshold),
        packCredits: selectedPack?.credits ?? 250,
        packPriceCents: selectedPack?.priceCents ?? 2500,
        stripePaymentMethodId: pmId || null,
      });
      if (!r.ok) throw new Error((await r.json().catch(() => ({}))).message || "Save failed");
      toast({ title: "Lead credit auto top-up saved" });
      refetch();
    } catch (e: any) {
      toast({ title: "Error", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  const cards = pmData?.methods ?? [];
  const totalCredits = (balData?.grantBalance ?? 0) + (balData?.purchasedBalance ?? 0);
  const lowBalance = totalCredits < 25 && !cfg?.enabled;

  return (
    <Card data-testid="card-lead-auto-top-up">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle className="text-base">Lead Credit Auto Top-Up</CardTitle>
              <CardDescription>Automatically purchase more credits when your lead balance runs low.</CardDescription>
            </div>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled} data-testid="switch-lead-auto-top-up" aria-label="Enable lead credit auto top-up" />
        </div>
      </CardHeader>
      {lowBalance && (
        <div className="px-6 pb-2">
          <Alert variant="destructive" className="py-2">
            <AlertDescription className="text-xs">
              Lead credit balance is low. Enable auto top-up to keep imports running.
            </AlertDescription>
          </Alert>
        </div>
      )}
      <CardContent className="space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Top up when credits below</Label>
            <Select value={threshold} onValueChange={setThreshold} disabled={!enabled}>
              <SelectTrigger data-testid="select-lead-threshold"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25 credits</SelectItem>
                <SelectItem value="50">50 credits</SelectItem>
                <SelectItem value="100">100 credits</SelectItem>
                <SelectItem value="250">250 credits</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Purchase pack</Label>
            <Select value={packIdx} onValueChange={setPackIdx} disabled={!enabled}>
              <SelectTrigger data-testid="select-lead-pack"><SelectValue /></SelectTrigger>
              <SelectContent>
                {packs.map((p, i) => (
                  <SelectItem key={p.priceCents} value={String(i)}>
                    {p.label} — {p.credits} credits · ${(p.priceCents / 100).toFixed(0)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Charge card</Label>
          {cards.length === 0 ? (
            <p className="text-xs text-muted-foreground">No saved card on file. Complete billing setup to add one.</p>
          ) : (
            <Select value={pmId} onValueChange={setPmId} disabled={!enabled}>
              <SelectTrigger data-testid="select-lead-pm"><SelectValue placeholder="Select a card" /></SelectTrigger>
              <SelectContent>
                {cards.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{cardLabel(c)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>
        {cfg?.last_triggered_at && (
          <p className="text-xs text-muted-foreground">Last topped up: {formatDate(cfg.last_triggered_at)}</p>
        )}
        <Button size="sm" onClick={handleSave} disabled={saving} data-testid="button-save-lead-auto-top-up">
          {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" />Saving…</> : "Save changes"}
        </Button>
      </CardContent>
    </Card>
  );
}
